import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

export const UNAPPROVED_PAY_LEAVE_NOTICE =
  "Pay not approved yet — it won't reach payroll until you Approve All.";

/**
 * A notice on the way OUT of the pay assignment screen, when rows are still
 * unapproved. It does not block navigation: leaving is allowed, it just is not
 * silent. Pay that is assigned and never approved never reaches payroll, and in
 * a live demo nobody noticed until the pay run was short.
 *
 * `pending` is read at unmount through a ref, so the value that counts is the
 * one on screen when the reader left, not the one at mount.
 *
 * THE TOAST IS DEFERRED ONE TICK AND CANCELLED BY A REMOUNT. React's
 * StrictMode unmounts and remounts every component once in development; with
 * pay assignments already cached, that simulated unmount would otherwise raise
 * the notice on ARRIVAL. The remount clears the timer before it fires, while a
 * real unmount never remounts and the timer runs.
 */
export function useUnapprovedPayLeaveNotice(pending: boolean) {
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (!pendingRef.current) return;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        toast.warning(UNAPPROVED_PAY_LEAVE_NOTICE);
      }, 0);
    };
  }, []);
}
